import React, { useEffect, useRef } from 'react';
import { format, isAfter, isBefore, startOfDay } from 'date-fns';
import { CalendarDays } from 'lucide-react';
import { calendarDay, localDay } from '@app/utils/helpers/date-utils';
import { Calendar } from '@ui/components/ui/calendar';
import { Label } from '@ui/components/ui/label';
import { cn } from '@ui/lib/utils';

export type WorkdayDateFieldProps = {
  /** Only needed where two of these share a screen, since the id is what the label points at. */
  id?: string;
  /** Null for a moment nobody has answered yet, which shows the placeholder rather than a day. */
  value: Date | null;
  /** The day picked, as midnight UTC — the app's calendar-day convention, safe to put straight on the wire. */
  onChange: (date: Date) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label: string;
  placeholder: string;
  errorMessage?: string;
  /** Colours the field without saying anything, for a caller printing the reason under it itself. */
  invalid?: boolean;
  disabledDays?: { before?: Date; after?: Date };
  /** Marks the label and the trigger as an answer the form will not go without. */
  required?: boolean;
  /** Which edge of the field the calendar hangs from. */
  align?: 'start' | 'end';
  className?: string;
};

/**
 * The day a report is filed against, as a button that opens a calendar under it.
 *
 * Shared by the add/edit entry modal and the incident form, so the two cannot disagree about how a day is picked.
 */
export function WorkdayDateField({
  id = 'report-modal-workday',
  value,
  onChange,
  open,
  onOpenChange,
  label,
  placeholder,
  errorMessage,
  invalid,
  disabledDays,
  required,
  align = 'end',
  className,
}: WorkdayDateFieldProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Portalled popovers (Base UI) sit outside Radix Dialog in the DOM; their clicks are treated
  // as "outside" the dialog, so the calendar never receives the day click reliably. Keep the
  // calendar in the same subtree as the dialog (absolute panel, no portal).
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onDocPointerDown = (e: PointerEvent) => {
      const t = e.target;
      if (!(t instanceof Node)) {
        return;
      }
      if (containerRef.current?.contains(t)) {
        return;
      }
      onOpenChange(false);
    };
    const onDocKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') {
        return;
      }
      // Capture phase: intercept before Base UI Dialog's document-level Escape listener.
      e.stopPropagation();
      onOpenChange(false);
    };
    document.addEventListener('pointerdown', onDocPointerDown, true);
    document.addEventListener('keydown', onDocKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown, true);
      document.removeEventListener('keydown', onDocKeyDown, true);
    };
  }, [open, onOpenChange]);

  // Compare by calendar day (isBefore / isAfter) so sprint / API instants and midnight-UTC do not break the [before, after] range.
  const isDateDisabled = (day: Date) => {
    const d0 = startOfDay(day);
    if (disabledDays?.before) {
      const b0 = startOfDay(disabledDays.before);
      if (isBefore(d0, b0)) {
        return true;
      }
    }
    if (disabledDays?.after) {
      const a0 = startOfDay(disabledDays.after);
      if (isAfter(d0, a0)) {
        return true;
      }
    }
    return false;
  };

  // `localDay`, not `value`: midnight UTC reads as yesterday west of Greenwich, filing the entry a day early.
  // NaN, not `instanceof`: an Invalid Date satisfies `Date`, and `format` throws on one.
  const day = value && !Number.isNaN(value.getTime()) ? localDay(value) : null;

  const display = day ? format(day, 'dd/MM/yyyy') : placeholder;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={id} required={required}>
        {label}
      </Label>
      <div className="relative w-full min-w-0" ref={containerRef}>
        <button
          id={id}
          type="button"
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-describedby={errorMessage ? `${id}-error` : undefined}
          onClick={() => onOpenChange(!open)}
          className={cn(
            'flex h-8 w-full min-w-0 items-center justify-between gap-2 rounded-lg border bg-transparent px-2.5! text-left text-sm shadow-xs outline-none transition-colors',
            'hover:bg-muted/50 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50',
            'dark:bg-input/30',
            errorMessage || invalid ? 'border-destructive' : 'border-input',
          )}
        >
          <span className="min-w-0 flex-1 truncate text-foreground">{display}</span>
          <CalendarDays className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        </button>
        {open ? (
          <div
            /* The panel is in the flow rather than portalled, so a box that clips it is a box that hides it. */
            ref={panel => panel?.scrollIntoView?.({ block: 'nearest' })}
            className={cn(
              'absolute top-full z-[200] mt-1 w-min min-w-64 rounded-md border border-border bg-popover p-0 text-popover-foreground shadow-md ring-1 ring-foreground/10',
              align === 'start' ? 'left-0' : 'right-0',
            )}
            role="presentation"
          >
            <Calendar
              className="p-2"
              mode="single"
              selected={day ?? undefined}
              defaultMonth={day ?? undefined}
              // `calendarDay`, not `startOfDay`: local midnight is the evening before in UTC for anyone east
              // of Greenwich, so a day picked in Brussels went on the wire as 22:00 the day before and the
              // server — which runs in UTC — drafted and filed against the wrong day.
              onSelect={date => {
                if (date) {
                  onChange(calendarDay(date));
                  onOpenChange(false);
                }
              }}
              disabled={isDateDisabled}
            />
          </div>
        ) : null}
      </div>
      {errorMessage ? (
        <p id={`${id}-error`} className="text-sm text-destructive" role="status">
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
}

export default WorkdayDateField;
